"use client";

import { useContext } from "react";
import { ArrowLeft } from "lucide-react";
import { DailyGameStageContext } from "@/features/daily/components/DailyGameStage";
import { cn } from "@/lib/utils";
import { useLocale } from "@/contexts/LocaleContext";

interface DailyChallengeHeaderProps {
  onQuit: () => void;
  currentIndex: number;
  total: number;
  timeLeft?: number;
  hideTimer?: boolean;
  /** Override the centre-pill label; defaults to "Question N/Total" */
  centerLabel?: string;
  /** Replaces the timer pill (e.g. a coins balance for untimed games). */
  rightSlot?: React.ReactNode;
  className?: string;
}

const poppins = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  letterSpacing: '0',
  lineHeight: 1,
} as const;

export function DailyChallengeHeader({
  onQuit,
  currentIndex,
  total,
  timeLeft,
  hideTimer = false,
  centerLabel,
  rightSlot,
  className,
}: DailyChallengeHeaderProps) {
  const { t } = useLocale();
  // In a stage the header lines up with the game column below it; elsewhere it keeps its own centred width.
  const inStage = useContext(DailyGameStageContext);
  const showTimer = !hideTimer && typeof timeLeft === "number";
  const displayTimer = showTimer
    ? (timeLeft! >= 10 ? `${timeLeft}` : `0${timeLeft}`)
    : null;
  const label = centerLabel ?? t("dailyGames.questionCounter", {
    current: String(Math.min(currentIndex + 1, total)),
    total: String(total),
  });

  return (
    <div className={cn("px-4 pt-4", className)}>
      <div className={cn("flex items-stretch gap-2.5", !inStage && "mx-auto max-w-3xl")}>
        <button
          type="button"
          onClick={onQuit}
          aria-label={t("common.back")}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-white/75 transition-colors hover:bg-white/10 hover:text-white sm:size-[52px]"
        >
          <ArrowLeft aria-hidden className="size-5 sm:size-6" />
        </button>
        <div
          className="flex flex-1 items-center justify-center rounded-[16px] bg-brand-blue px-5 text-white h-[40px] sm:h-[52px]"
          style={{ ...poppins, fontSize: 'clamp(14px, 2.2vw, 26px)' }}
        >
          {label}
        </div>
        {rightSlot ? (
          <div
            className="flex min-w-[64px] items-center justify-center gap-1.5 rounded-[16px] bg-brand-blue px-3 text-white h-[40px] sm:h-[52px] sm:min-w-[92px] tabular-nums"
            style={{ ...poppins, fontSize: 'clamp(14px, 2.2vw, 26px)' }}
          >
            {rightSlot}
          </div>
        ) : null}
        {rightSlot === undefined && displayTimer !== null && (
          <div
            className="flex w-[64px] items-center justify-center rounded-[16px] bg-brand-blue text-white h-[40px] sm:h-[52px] sm:w-[92px] tabular-nums"
            style={{ ...poppins, fontSize: 'clamp(14px, 2.2vw, 26px)' }}
          >
            {displayTimer}
          </div>
        )}
      </div>
    </div>
  );
}
